import { GROUP_SEARCH_PRESELECTION_QUERY_PARAM_KEYS } from 'client-portal/utils/clinicianSearch/facets/manifest';
import {
  clinicianSearchFilterSeedQueryParams,
  seedClinicianSearchFiltersFromPreselection,
} from 'client-portal/utils/clinicianSearch/seed-clinician-search-filters';
import Service, { service } from '@ember/service';

export default class ClinicianSearchPreselectionNavigationService extends Service {
  @service clinicianSearch;
  @service store;

  // ---------- state ----------

  originalParams = null;
  skippedLeaves = new Set();
  interceptorRouter = null;

  get isInSession() {
    return this.skippedLeaves.size > 0;
  }

  // ---------- session ----------

  beginSession(queryParams, transition) {
    if (!this.clinicianSearch.isEnabled) return;

    let leafName = transition?.to?.name?.split('.').pop();
    if (!this.originalParams) {
      this.originalParams = { ...(queryParams ?? {}) };
    }
    this.recordSkippedLeaf(leafName);
  }

  restoreFromRoute({ transition, controller, reservation } = {}) {
    if (!this.clinicianSearch.isEnabled) return;

    let queryParams = this.clinicianSearch.preselection.paramsFromUrl(transition, {
      controller,
    });
    if (this.isInSession || !this.clinicianSearch.preselection.isActiveParams(queryParams)) {
      return;
    }

    this.originalParams = { ...(queryParams ?? {}) };

    if (reservation?.belongsTo('cptCode')?.id()) {
      this.recordSkippedLeaf('service');
    }
    if (reservation?.belongsTo('office')?.id()) {
      this.recordSkippedLeaf('location');
    }
    this.recordSkippedLeaf('date');
  }

  recordSkippedLeaf(leafName) {
    if (!this.clinicianSearch.isEnabled) return;

    if (leafName) {
      this.skippedLeaves.add(leafName);
    }
  }

  endSession() {
    this.originalParams = null;
    this.skippedLeaves = new Set();
  }

  resetState() {
    this.endSession();
    this.uninstallBackInterceptor();
  }

  activate({ router, reservation, controller } = {}) {
    if (!this.clinicianSearch.isEnabled) return;

    this.restoreFromRoute({ controller, reservation });
    this.installBackInterceptor(router);
  }

  deactivate() {
    if (!this.clinicianSearch.isEnabled) return;

    this.uninstallBackInterceptor();
  }

  // ---------- back interceptor ----------

  installBackInterceptor(router) {
    if (this.interceptorRouter === router) {
      return;
    }
    this.uninstallBackInterceptor();
    this.interceptorRouter = router;
    router.on('routeWillChange', this.onRouteWillChange);
  }

  uninstallBackInterceptor() {
    if (!this.interceptorRouter) {
      return;
    }
    this.interceptorRouter.off('routeWillChange', this.onRouteWillChange);
    this.interceptorRouter = null;
  }

  onRouteWillChange = transition => {
    if (!this.clinicianSearch.isEnabled) return;

    if (this.isInSession && !this.clinicianSearch.preselection.isActiveTransition(transition)) {
      this.endSession();
      return;
    }

    if (!this.isInSession) {
      return;
    }

    let fromName = transition.from?.name ?? null;
    let toName = transition.to?.name ?? '';
    let toLeaf = toName.split('.').pop();

    if (fromName === toName) {
      return;
    }
    if (!this.skippedLeaves.has(toLeaf)) {
      return;
    }

    let targetName = `${this.baseRouteFor(toName)}.clinician-search`;
    if (toName === targetName) {
      return;
    }

    let queryParams = this.redirectQueryParams();
    let router = this.interceptorRouter;
    this.endSession();
    transition.abort();
    router?.replaceWith(targetName, { queryParams });
  };

  // ---------- private ----------

  redirectQueryParams() {
    if (!this.clinicianSearch.isEnabled) return;

    let seeded = seedClinicianSearchFiltersFromPreselection(this.originalParams ?? {}, {
      store: this.store,
    });
    let clearedPreselection = Object.fromEntries(
      GROUP_SEARCH_PRESELECTION_QUERY_PARAM_KEYS.map(key => [key, null])
    );
    return {
      ...clinicianSearchFilterSeedQueryParams(seeded),
      ...clearedPreselection,
      clinicianId: null,
    };
  }

  baseRouteFor(toName) {
    return toName.startsWith('widget') ? 'widget' : 'site.request';
  }
}
